/* ========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* ========================================================================== */
/*
 * TemplateManager.jsx — ปุ่ม "จัดการเทมเพลต" + หน้าต่างเพิ่ม/แก้/ซ่อนเทมเพลตภาพ AI ของร้าน (20 ต.ค. 2569)
 *   ใช้ร่วม 2 หน้า: ภาพสินค้า AI (scope product · เรียก "เทมเพลต") · กราฟิกแคมเปญ (scope campaign · เรียก "สไตล์")
 *   โหลดสิทธิ์เอง (GET /ai_template) — ไม่มีสิทธิ์ ai_template = ไม่แสดงอะไรเลย หน้าแม่ไม่ต้องเช็ค
 *   ชุดตั้งต้นของระบบซ่อน/แสดงได้อย่างเดียว · ของร้านแก้/ลบได้ · เปลี่ยนแล้วเรียก onChanged() ให้หน้าแม่โหลดรายการใหม่
 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import Chip from '@mui/material/Chip';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import Resizer from 'react-image-file-resizer';
import { FetchData } from 'services/Request';
import promptLength from 'layouts/_entrio/promptLength';

const MOD = 'ai_template';
const IMAGE_MAX = 6 * 1024 * 1024;
const ERRORS = {
  permission_denied: 'บัญชีนี้ไม่มีสิทธิ์จัดการเทมเพลต',
  migration_missing: 'ระบบยังไม่พร้อมให้เพิ่มเทมเพลต แจ้งไอที',
  template_name_invalid: 'ใส่ชื่อไม่เกิน 100 ตัวอักษร',
  template_prompt_empty: 'ใส่คำสั่งก่อนบันทึก',
  ai_prompt_too_long: 'คำสั่งยาวเกิน 2,000 ตัวอักษร',
  template_description_too_long: 'คำอธิบายยาวเกิน 255 ตัวอักษร',
  template_image_type: 'ใช้รูป JPG PNG หรือ WEBP',
  template_image_too_large: 'รูปใหญ่เกิน 6 MB',
  template_image_save_failed: 'เก็บรูปไม่สำเร็จ แจ้งไอที',
  template_builtin_locked: 'ชุดตั้งต้นแก้ไม่ได้ ซ่อนได้อย่างเดียว',
  template_not_found: 'ไม่พบรายการนี้แล้ว โหลดหน้าใหม่',
};
const errText = (msg) => ERRORS[msg] || 'บันทึกไม่สำเร็จ ลองอีกครั้ง';
const resize = (file) =>
  new Promise((resolve) => {
    Resizer.imageFileResizer(file, 600, 600, 'JPEG', 88, 0, (uri) => resolve(uri), 'base64');
  });

function TemplateManager({ scope, app, defaultPrompt, onChanged, onNotify }) {
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const fileRef = useRef(null);
  const alive = useRef(true);
  const word = scope === 'campaign' ? 'สไตล์' : 'เทมเพลต';

  useEffect(() => {
    alive.current = true;
    FetchData('GET', `/${MOD}`, app.token, {})
      .then((r) => {
        if (alive.current && r.status === 'ok') setMeta(r);
      })
      .catch(() => {});
    return () => {
      alive.current = false;
    };
  }, []);

  const loadList = () =>
    FetchData('POST', `/${MOD}`, app.token, { scope, _t: Date.now() })
      .then((r) => {
        if (!alive.current) return;
        if (r.status === 'ok') setList(r.data || []);
        else onNotify(errText(r.msg), 'error');
      })
      .catch(() => alive.current && onNotify('โหลดรายการไม่สำเร็จ', 'error'));

  if (!meta || meta.can_manage !== 'Y') return null;

  const categories = (meta.categories && meta.categories[scope]) || [meta.own_category];
  const promptMax = meta.prompt_max || 2000;

  const openManager = () => {
    setList(null);
    setOpen(true);
    loadList();
  };
  const startAdd = () =>
    setForm({
      name: '',
      category: meta.own_category,
      description: '',
      prompt: (defaultPrompt || '').trim(),
      image: '',
      example: '',
      removeImage: false,
    });
  const startEdit = (row) =>
    setForm({
      id: row.id,
      name: row.name,
      category: row.category,
      description: row.description || '',
      prompt: row.prompt,
      image: '',
      example: row.example,
      removeImage: false,
    });

  const changed = (text) => {
    onNotify(text, 'success');
    loadList();
    onChanged();
  };

  const toggle = (row) => {
    setBusy(true);
    FetchData('PATCH', `/${MOD}/${row.id}`, app.token, {
      enable: row.enable === 'Y' ? 'N' : 'Y',
      _t: Date.now(),
    })
      .then((r) => {
        if (!alive.current) return;
        setBusy(false);
        if (r.status === 'ok')
          changed(row.enable === 'Y' ? `ซ่อน "${row.name}" แล้ว` : `แสดง "${row.name}" แล้ว`);
        else onNotify(errText(r.msg), 'error');
      })
      .catch(() => {
        if (!alive.current) return;
        setBusy(false);
        onNotify(errText(''), 'error');
      });
  };

  const pickImage = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > IMAGE_MAX) {
      onNotify(ERRORS.template_image_too_large, 'warning');
      return;
    }
    const uri = await resize(file);
    if (alive.current) setForm((f) => f && { ...f, image: uri, removeImage: false });
  };

  const promptCount = form ? promptLength(form.prompt) : 0;
  const canSave =
    form && !busy && form.name.trim() !== '' && promptCount > 0 && promptCount <= promptMax;

  const save = () => {
    if (!canSave) return;
    setBusy(true);
    const body = {
      scope,
      name: form.name.trim(),
      category: form.category,
      description: form.description.trim(),
      prompt: form.prompt.trim(),
      _t: Date.now(),
    };
    if (form.image) body.image = form.image;
    if (form.removeImage) body.remove_image = 'Y';
    const req = form.id
      ? FetchData('PATCH', `/${MOD}/${form.id}`, app.token, body)
      : FetchData('PUT', `/${MOD}`, app.token, body);
    req
      .then((r) => {
        if (!alive.current) return;
        setBusy(false);
        if (r.status === 'ok') {
          setForm(null);
          changed(`บันทึก "${body.name}" แล้ว`);
        } else onNotify(errText(r.msg), 'error');
      })
      .catch(() => {
        if (!alive.current) return;
        setBusy(false);
        onNotify(errText(''), 'error');
      });
  };

  const remove = () => {
    setBusy(true);
    FetchData('DELETE', `/${MOD}/${form.id}`, app.token, {})
      .then((r) => {
        if (!alive.current) return;
        setBusy(false);
        setConfirmDel(false);
        if (r.status === 'ok') {
          const { name } = form;
          setForm(null);
          changed(`ลบ "${name}" แล้ว`);
        } else onNotify(errText(r.msg), 'error');
      })
      .catch(() => {
        if (!alive.current) return;
        setBusy(false);
        onNotify('ลบไม่สำเร็จ ลองอีกครั้ง', 'error');
      });
  };

  const preview = form && (form.image || (form.removeImage ? '' : form.example));

  return (
    <>
      <MDButton variant='text' color='info' size='small' onClick={openManager} sx={{ mr: 'auto' }}>
        <Icon>tune</Icon>&nbsp;จัดการ{word}
      </MDButton>

      {/* ── รายการทั้งหมดของหน้านี้ รวมที่ซ่อน ───────────────────────── */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth='md'>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          จัดการ{word}
          <MDBox flexGrow={1} />
          <MDButton variant='gradient' color='info' size='small' onClick={startAdd}>
            <Icon>add</Icon>&nbsp;เพิ่ม{word}
          </MDButton>
        </DialogTitle>
        <DialogContent dividers>
          {list === null && (
            <MDTypography variant='button' color='text'>
              กำลังโหลด…
            </MDTypography>
          )}
          {list &&
            list.map((row) => (
              <MDBox
                key={row.id}
                display='flex'
                alignItems='center'
                gap={1.5}
                py={1}
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'grey.200',
                  opacity: row.enable === 'Y' ? 1 : 0.55,
                }}
              >
                <MDBox
                  sx={{
                    width: 64,
                    height: 48,
                    flexShrink: 0,
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: 'grey.200',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {row.example ? (
                    <img
                      src={row.example}
                      alt=''
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <Icon sx={{ color: 'grey.500' }}>image</Icon>
                  )}
                </MDBox>
                <MDBox flex={1} minWidth={0}>
                  <MDBox display='flex' alignItems='center' gap={0.75} flexWrap='wrap'>
                    <MDTypography variant='button' fontWeight='bold'>
                      {row.name}
                    </MDTypography>
                    {row.custom === 'Y' && <Chip size='small' color='info' label='ของร้าน' />}
                    {row.enable !== 'Y' && (
                      <Chip size='small' variant='outlined' label='ซ่อนอยู่' />
                    )}
                  </MDBox>
                  <MDTypography variant='caption' color='text' display='block' noWrap>
                    {row.category}
                    {row.description ? ` · ${row.description}` : ''}
                  </MDTypography>
                </MDBox>
                <Switch
                  checked={row.enable === 'Y'}
                  disabled={busy}
                  onChange={() => toggle(row)}
                  inputProps={{ 'aria-label': `แสดง ${row.name}` }}
                />
                {row.custom === 'Y' ? (
                  <IconButton
                    size='small'
                    aria-label={`แก้ ${row.name}`}
                    title='แก้'
                    onClick={() => startEdit(row)}
                  >
                    <Icon fontSize='small'>edit</Icon>
                  </IconButton>
                ) : (
                  <MDBox width={34} title='ชุดตั้งต้น ซ่อนได้อย่างเดียว' />
                )}
              </MDBox>
            ))}
        </DialogContent>
        <DialogActions>
          <MDButton color='dark' onClick={() => setOpen(false)}>
            ปิด
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ── เพิ่ม/แก้ ──────────────────────────────────────────────── */}
      <Dialog
        open={Boolean(form)}
        onClose={busy ? undefined : () => setForm(null)}
        fullWidth
        maxWidth='sm'
        aria-labelledby='template-form-title'
      >
        <DialogTitle id='template-form-title'>
          {form && form.id ? `แก้${word}` : `เพิ่ม${word}`}
        </DialogTitle>
        <DialogContent dividers>
          {form && (
            <MDBox display='flex' flexDirection='column' gap={2} pt={0.5}>
              <MDInput
                label='ชื่อ'
                fullWidth
                autoFocus
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                inputProps={{ maxLength: 100 }}
                InputLabelProps={{ shrink: true }}
              />
              <MDInput
                label='หมวด'
                select
                fullWidth
                value={categories.includes(form.category) ? form.category : meta.own_category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                InputLabelProps={{ shrink: true }}
              >
                {categories.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </MDInput>
              <MDInput
                label='คำอธิบายสั้น (ถ้ามี)'
                fullWidth
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                inputProps={{ maxLength: 255 }}
                InputLabelProps={{ shrink: true }}
              />
              <MDInput
                label='คำสั่งที่ส่งให้ AI'
                fullWidth
                multiline
                minRows={5}
                value={form.prompt}
                onChange={(e) => setForm({ ...form, prompt: e.target.value })}
                placeholder={
                  scope === 'campaign'
                    ? 'บอกแนวภาพ เช่น โทนสี บรรยากาศ ตัวอักษร · ชื่อแคมเปญ ราคา สินค้า วันที่ ระบบเติมให้เอง'
                    : 'บอกว่าอยากได้ภาพแบบไหน เช่น วางบนโต๊ะไม้ แสงธรรมชาติ ใช้สินค้าจริงจากรูปที่แนบ'
                }
                error={promptCount > promptMax}
                helperText={
                  promptCount > promptMax * 0.9
                    ? `${promptCount.toLocaleString()} / ${promptMax.toLocaleString()} ตัวอักษร`
                    : ''
                }
                InputLabelProps={{ shrink: true }}
              />
              <MDBox display='flex' alignItems='center' gap={1.5}>
                <MDBox
                  sx={{
                    width: 120,
                    height: 80,
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    bgcolor: 'grey.100',
                    border: '1px dashed',
                    borderColor: 'grey.400',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {preview ? (
                    <img
                      src={preview}
                      alt='รูปตัวอย่าง'
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <Icon sx={{ color: 'grey.500' }}>image</Icon>
                  )}
                </MDBox>
                <MDBox display='flex' flexDirection='column' alignItems='flex-start' gap={0.5}>
                  <MDTypography variant='caption' color='text'>
                    รูปตัวอย่าง (ถ้ามี)
                  </MDTypography>
                  <MDBox display='flex' gap={1}>
                    <MDButton
                      variant='outlined'
                      color='info'
                      size='small'
                      onClick={() => fileRef.current && fileRef.current.click()}
                    >
                      <Icon>upload</Icon>&nbsp;เลือกรูป
                    </MDButton>
                    {preview && (
                      <MDButton
                        variant='text'
                        color='error'
                        size='small'
                        onClick={() =>
                          setForm({ ...form, image: '', removeImage: Boolean(form.id) })
                        }
                      >
                        เอารูปออก
                      </MDButton>
                    )}
                  </MDBox>
                </MDBox>
                <input
                  ref={fileRef}
                  type='file'
                  accept='image/jpeg,image/png,image/webp'
                  style={{ display: 'none' }}
                  onChange={pickImage}
                />
              </MDBox>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          {form && form.id && (
            <MDButton
              variant='text'
              color='error'
              disabled={busy}
              onClick={() => setConfirmDel(true)}
              sx={{ mr: 'auto' }}
            >
              <Icon>delete</Icon>&nbsp;ลบ
            </MDButton>
          )}
          <MDButton
            variant='outlined'
            color='secondary'
            disabled={busy}
            onClick={() => setForm(null)}
          >
            ยกเลิก
          </MDButton>
          <MDButton variant='gradient' color='info' disabled={!canSave} onClick={save}>
            {busy ? 'กำลังบันทึก…' : 'บันทึก'}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ── ยืนยันลบ ──────────────────────────────────────────────── */}
      <Dialog
        open={confirmDel}
        onClose={busy ? undefined : () => setConfirmDel(false)}
        maxWidth='xs'
      >
        <DialogTitle>ลบ{word}นี้?</DialogTitle>
        <DialogContent>
          <MDTypography variant='body2'>
            {form
              ? `"${form.name}" จะหายจากรายการให้เลือก ถ้าแค่ไม่อยากให้เห็นชั่วคราว ใช้ปิดสวิตช์แสดงแทน`
              : ''}
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton
            autoFocus
            variant='outlined'
            color='secondary'
            onClick={() => setConfirmDel(false)}
          >
            ยกเลิก
          </MDButton>
          <MDButton variant='gradient' color='error' disabled={busy} onClick={remove}>
            ลบ
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

TemplateManager.defaultProps = {
  defaultPrompt: '',
};
TemplateManager.propTypes = {
  scope: PropTypes.oneOf(['product', 'campaign']).isRequired,
  app: PropTypes.shape({ token: PropTypes.string }).isRequired,
  defaultPrompt: PropTypes.string, // หน้าภาพสินค้า AI ส่งคำสั่งที่พิมพ์อยู่ → เพิ่มใหม่เริ่มจากคำสั่งนั้น
  onChanged: PropTypes.func.isRequired,
  onNotify: PropTypes.func.isRequired,
};

export default TemplateManager;
